import { useTranslation } from 'react-i18next';
import { useCreator } from './useCreator';

export interface ContactableBrand {
  brand_id: string;
  name: string;
  handle?: string | null;
  can_message?: boolean;   // legacy field from the RPCs; in-app brand chat was retired 28.10
  posts?: number | null;   // only known for brands they've already worked with
}

/**
 * One way for a creator to reach a business, used everywhere a brand is shown: a share the
 * creator sends themselves (native sheet or WhatsApp) with a link to their public card.
 * The in-app brand chat was retired with brand ownership (28.10.2026) — no brand "owns" a
 * page any more, so there is nobody on the other side of a thread (0 threads were ever opened).
 */
export function BrandContact({ brand, compact }: { brand: ContactableBrand; compact?: boolean }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o }) as string;
  const { provider } = useCreator();

  const share = async () => {
    const text = brand.posts
      ? tr('creator.brands.shareText',
          'Здравствуйте! Мы уже работали — я делал(а) {{n}} публикаций о {{brand}}. Вот моя статистика в AdaMar:',
          { n: brand.posts, brand: brand.name })
      : tr('creator.brands.pitchText',
          'Здравствуйте! Я блогер, работаю с брендами в вашей нише. Вот моя статистика в AdaMar:');
    // Link to the creator's own public media kit so the brand actually sees their stats,
    // not just the homepage. Falls back to the homepage if the handle isn't loaded yet.
    const url = provider?.handle ? `https://adamar.app/c/${provider.handle}` : 'https://adamar.app';
    try {
      if (navigator.share) await navigator.share({ title: brand.name, text, url });
      else window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank');
    } catch { /* share sheet dismissed */ }
  };

  const base: React.CSSProperties = {
    borderRadius: 7, padding: compact ? '4px 10px' : '5px 12px',
    fontSize: compact ? 11.5 : 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  };

  return (
    <button type="button" onClick={share}
      style={{ ...base, background: 'transparent', border: '1px solid rgba(255,255,255,0.16)', color: 'var(--muted)' }}>
      {tr('creator.brands.share', 'Отправить о себе ↗')}
    </button>
  );
}
